// src/pages/personal/analytics/MarketingExpensesSection/components/MarketingMetrics.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, MetricCard } from '@/components/ui/Layout';
import { TEXT_STYLES } from '@/theme';
import { 
  formatNumber, 
  formatPercent, 
  formatPrice 
} from '@/utils/Formatters';
import { NUMBER_FORMATS, LOCALES } from '@/utils/constants';
import { MarketingMetrics as MarketingMetricsType } from '../hooks/useMarketingExpensesData';
import { MarketingMetricType } from '../MarketingExpensesSection';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';

interface MarketingMetricsProps {
  metrics: MarketingMetricsType | null;
  onSelectMetric: (metric: MarketingMetricType) => void;
  selectedMetric: MarketingMetricType;
}

const MarketingMetrics: React.FC<MarketingMetricsProps> = ({ 
  metrics, 
  onSelectMetric,
  selectedMetric
}) => {
  const { t, i18n } = useTranslation(['expenses', 'common']);
  const { currencySymbol } = useBusinessCurrency();
  const currentLocale = i18n.language || LOCALES.DEFAULT;
  
  // Если метрики не переданы, не рендерим компонент
  if (!metrics) return null;
  
  // Функция для форматирования значения в зависимости от типа метрики
  const formatMetricValue = (type: MarketingMetricType, value: number): string => {
    switch (type) {
      case 'totalSpend':
        return formatPrice(value, currencySymbol, currentLocale);
      case 'impressions':
      case 'clicks':
      case 'uniqueReach':
        return formatNumber(value, NUMBER_FORMATS.DEFAULT_PRECISION, true, currentLocale);
      case 'ctr':
        return formatPercent(value / 100, NUMBER_FORMATS.PERCENT_PRECISION, true, currentLocale);
      default:
        return formatNumber(value, NUMBER_FORMATS.DEFAULT_PRECISION, true, currentLocale);
    }
  };
  
  // Определяем является ли рост "хорошим" для каждой метрики
  const isIncreasePositive = (metricType: MarketingMetricType): boolean => {
    switch (metricType) {
      case 'impressions':
      case 'clicks':
      case 'uniqueReach':
      case 'ctr':
        return true; // Рост этих метрик - это хорошо
      case 'totalSpend':
        return false; // Для расходов рост обычно считается отрицательным
      default:
        return true;
    }
  };
  
  // Получение правильного направления тренда (с учетом "хорошести" роста)
  const getTrendDirection = (metricType: MarketingMetricType, change: number): 'up' | 'down' | 'neutral' => {
    if (change === 0) return 'neutral';
    
    const isPositive = change > 0;
    const isGoodToIncrease = isIncreasePositive(metricType);
    
    // Если метрика растет, и рост - это хорошо, ИЛИ метрика падает, и рост - это плохо
    if ((isPositive && isGoodToIncrease) || (!isPositive && !isGoodToIncrease)) {
      return 'up'; // Положительный тренд
    } else {
      return 'down'; // Отрицательный тренд
    }
  };
  
  return (
    <div>
      {/* Заголовок с метриками */}
      <div className="bg-[#F5FAF5] px-5 py-3 rounded-t-lg border border-[#e5e7eb]">
        <h2 className={TEXT_STYLES.h2}>{t('marketing.metrics.title')}</h2>
      </div>
      
      {/* Карточки с метриками */}
      <div className="bg-white px-5 py-4 rounded-b-lg border-x border-b border-[#e5e7eb]">
        <Grid 
          cols={5} 
          mobileCols={2} 
          tabletCols={2} 
          gap="md"
          className="metric-grid"
        >
          {Object.entries(metrics).map(([key, metric]) => {
            const metricKey = key as MarketingMetricType;
            
            // Безопасное получение значения
            const value = metric && typeof metric.value !== 'undefined' ? metric.value : 0;
            const change = metric && typeof metric.change !== 'undefined' ? metric.change : 0;
            
            // Определяем направление тренда с учетом "хорошести" роста
            const trendDirection = getTrendDirection(metricKey, change);
            
            return (
              <MetricCard
                key={key}
                title={t(`marketing.metrics.${key}`)}
                value={formatMetricValue(metricKey, value)}
                trend={trendDirection}
                trendValue={Math.abs(change)} // Абсолютное значение изменения
                isSelected={selectedMetric === metricKey}
                onClick={() => onSelectMetric(metricKey)}
              />
            );
          })}
        </Grid>
      </div>
    </div>
  );
};

export default MarketingMetrics;